import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './AnalysisGrid.css';
import api from '../api/axiosConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format, isValid, startOfMonth, subMonths, endOfMonth } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// 숫자 포맷
const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '0';
  const number = parseFloat(num.toString().replace(/,/g, ''));
  if (isNaN(number) || number === 0) return '0';
  return number.toLocaleString();
};

// 숫자만 추출 (엑셀용)
const parseNumberForExcel = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const number = parseFloat(num.toString().replace(/,/g, ''));
  return isNaN(number) ? 0 : number;
};

const Total1 = () => {
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);

    const today = new Date();
    const threeMonthsAgo = subMonths(today, 3);
    const [startDate, setStartDate] = useState(startOfMonth(threeMonthsAgo));
    const [endDate, setEndDate] = useState(endOfMonth(today));

    const [fieldId, setFieldId] = useState('');
    const [fieldOptions, setFieldOptions] = useState([]);

    const [rowsWithSubtotal, setRowsWithSubtotal] = useState([]);

    // 합계 상태 분리
    const [totalRow, setTotalRow] = useState({});
    const [taxInvoiceRow, setTaxInvoiceRow] = useState({});
    const [etcRow, setEtcRow] = useState({});

    // 현장 조회
    useEffect(() => {
        api.get(`/api/fieldList`)
            .then((res) => {
                const uniqueOptions = res.data.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.FIELD_ID === item.FIELD_ID)
                );
                setFieldOptions(uniqueOptions);
            })
            .catch((err) => console.error(err));
    }, []);

    // 매출정산서 현황 조회
    const searchList = useCallback(() => {
        const start = format(startDate, 'yyyy-MM-dd');
        const end = format(endDate, 'yyyy-MM-dd');

        api.get(`/api/analysis/searchList3?startDate=${start}&endDate=${end}&fieldId=${fieldId || ''}`)
            .then((res) => {
                const rawData = res.data || [];
                const processedData = rawData.map(row => {
                    let month = row.ISSUED_MONTH;
                    let day = row.ISSUED_DAY;
                    if (!month && row.ISSUED_DATE && row.ISSUED_DATE.length >= 10) {
                        month = row.ISSUED_DATE.substring(5, 7);
                        day = row.ISSUED_DATE.substring(8, 10);
                    }
                    return {
                        ...row,
                        ISSUED_MONTH: month,
                        ISSUED_DAY: day,
                        ETC_YN: row.ETC_YN || 'N'
                    };
                });
                setData(processedData);
                setRows(processedData);
            })
            .catch((err) => console.error(err));
    }, [startDate, endDate, fieldId]);

    useEffect(() => {
        searchList();
    }, [searchList]);

    // 소계/월계/총계 및 분리 합계 계산 로직
    const calculateSubtotals = (list) => {
        if (!list || list.length === 0) {
            setRowsWithSubtotal([]);
            setTotalRow({});
            setTaxInvoiceRow({});
            setEtcRow({});
            return;
        }

        const result = [];
        const createZeroObj = () => ({
            OUT_PROVIDE_AMOUNT: 0, OUT_VAT: 0, OUT_TOTAL_AMOUNT: 0, UNPAID_AMOUNT_OUT: 0, PAID_AMOUNT: 0
        });

        const accumulate = (target, source) => {
            for (const key in target) {
                target[key] += (parseFloat(source[key]) || 0);
            }
        };

        let currentGroupTotal = createZeroObj();
        let monthlyTotal = createZeroObj();

        let grandTotal = createZeroObj();
        let taxSum = createZeroObj();
        let etcSum = createZeroObj();

        let currentMonth = list[0].ISSUED_MONTH;
        let currentETC = list[0].ETC_YN;

        list.forEach((row) => {
            const rowMonth = row.ISSUED_MONTH;
            const rowETC = row.ETC_YN;

            result.push(row);
            accumulate(currentGroupTotal, row);

            if (row.ETC_YN === 'Y') {
                accumulate(etcSum, row);
            } else {
                accumulate(taxSum, row);
            }
        });

        setRowsWithSubtotal(result);
        setTotalRow(grandTotal);
        setTaxInvoiceRow(taxSum);
        setEtcRow(etcSum);
    };

    useEffect(() => {
        calculateSubtotals(rows);
    }, [rows]);

    // =========================
    // 📊 엑셀 내려받기
    // =========================

    const exportToExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('매출정산서');

            const fieldName = fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || '전체';
            let rowNum = 1;

            // ═══════════════════════════════════════════════════════════
            // 1️⃣ 제목
            // ═══════════════════════════════════════════════════════════
            worksheet.mergeCells(`A${rowNum}:G${rowNum}`);
            const titleCell = worksheet.getCell(`A${rowNum}`);
            titleCell.value = '매출 정산서';
            titleCell.font = { size: 20, name: '굴림체' };
            titleCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
            worksheet.getRow(rowNum).height = 45;
            rowNum++;

            // ═══════════════════════════════════════════════════════════
            // 2️⃣ 현장명
            // ═══════════════════════════════════════════════════════════
            worksheet.mergeCells(`A${rowNum}:G${rowNum}`);
            const fieldCell = worksheet.getCell(`A${rowNum}`);
            fieldCell.value = `현장명: ${fieldName}`;
            fieldCell.font = { size: 10, name: '굴림체' };
            fieldCell.alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getRow(rowNum).height = 22;
            rowNum++;

            // ═══════════════════════════════════════════════════════════
            // 열 너비 설정 (7컬럼: A~G)
            // A: 청구내역/계약금액라벨
            // B: 날짜/합계라벨
            // C: 공급가액
            // D: 부가세
            // E: 합계
            // F: 입금일자
            // G: 입금액
            // ═══════════════════════════════════════════════════════════
            worksheet.columns = [
                { width: 12 }, // A
                { width: 15 }, // B
                { width: 18 }, // C
                { width: 15 }, // D
                { width: 18 }, // E
                { width: 15 }, // F
                { width: 15 }, // G
            ];

            // ═══════════════════════════════════════════════════════════
            // 헤더 1행
            // A1:B1 병합 → "계약금액" (rowspan=2에 해당)
            // C1: 공급가액
            // D1: 부가세
            // E1: 합계
            // F1:F2 병합 → "입금일자" (green, rowspan=2에 해당)
            // G1:G2 병합 → "입금액"   (green, rowspan=2에 해당)
            // ═══════════════════════════════════════════════════════════
            const headerRow1 = rowNum;

            // A1:B1 병합 - "계약금액"
            worksheet.mergeCells(`A${rowNum}:B${rowNum + 1}`);
            const contractLabelCell = worksheet.getCell(`A${rowNum}`);
            contractLabelCell.value = '계약금액';
            contractLabelCell.font = { bold: true, size: 10, name: '굴림체' };
            contractLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
            contractLabelCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // C1: 공급가액
            const supplyHeaderCell = worksheet.getCell(`C${rowNum}`);
            supplyHeaderCell.value = '공 급 가 액';
            supplyHeaderCell.font = { bold: true, size: 10, name: '굴림체' };
            supplyHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
            supplyHeaderCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // D1: 부가세
            const vatHeaderCell = worksheet.getCell(`D${rowNum}`);
            vatHeaderCell.value = '부 가 세';
            vatHeaderCell.font = { bold: true, size: 10, name: '굴림체' };
            vatHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
            vatHeaderCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // E1: 합계
            const totalHeaderCell = worksheet.getCell(`E${rowNum}`);
            totalHeaderCell.value = '합 계';
            totalHeaderCell.font = { bold: true, size: 10, name: '굴림체' };
            totalHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
            totalHeaderCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // F1:F2 병합 - "입금일자" (green)
            worksheet.mergeCells(`F${rowNum}:F${rowNum + 1}`);
            const paidDateHeaderCell = worksheet.getCell(`F${rowNum}`);
            paidDateHeaderCell.value = '입금일자';
            paidDateHeaderCell.font = { size: 10, name: '굴림체' };
            paidDateHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
            paidDateHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA8E6A8' } };
            paidDateHeaderCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // G1:G2 병합 - "입금액" (green)
            worksheet.mergeCells(`G${rowNum}:G${rowNum + 1}`);
            const paidAmtHeaderCell = worksheet.getCell(`G${rowNum}`);
            paidAmtHeaderCell.value = '입금액';
            paidAmtHeaderCell.font = { size: 10, name: '굴림체' };
            paidAmtHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
            paidAmtHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA8E6A8' } };
            paidAmtHeaderCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            worksheet.getRow(rowNum).height = 22;
            rowNum++;

            // ═══════════════════════════════════════════════════════════
            // 헤더 2행: 계약금액 실제 값 (green)
            // C2: rows[0].PROVIDE_AMOUNT
            // D2: rows[0].VAT
            // E2: rows[0].TOTAL_AMOUNT
            // ═══════════════════════════════════════════════════════════
            const contractProvide = rows.length > 0 ? parseNumberForExcel(rows[0].PROVIDE_AMOUNT) : 0;
            const contractVat     = rows.length > 0 ? parseNumberForExcel(rows[0].VAT)            : 0;
            const contractTotal   = rows.length > 0 ? parseNumberForExcel(rows[0].TOTAL_AMOUNT)   : 0;

            const contractValueC = worksheet.getCell(`C${rowNum}`);
            contractValueC.value = contractProvide;
            contractValueC.numFmt = '#,##0';
            contractValueC.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA8E6A8' } };
            contractValueC.font = { bold: true, name: '굴림체', size: 10 };
            contractValueC.alignment = { horizontal: 'right', vertical: 'middle' };
            contractValueC.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            const contractValueD = worksheet.getCell(`D${rowNum}`);
            contractValueD.value = contractVat;
            contractValueD.numFmt = '#,##0';
            contractValueD.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA8E6A8' } };
            contractValueD.font = { bold: true, name: '굴림체', size: 10 };
            contractValueD.alignment = { horizontal: 'right', vertical: 'middle' };
            contractValueD.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            const contractValueE = worksheet.getCell(`E${rowNum}`);
            contractValueE.value = contractTotal;
            contractValueE.numFmt = '#,##0';
            contractValueE.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA8E6A8' } };
            contractValueE.font = { bold: true, name: '굴림체', size: 10 };
            contractValueE.alignment = { horizontal: 'right', vertical: 'middle' };
            contractValueE.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            worksheet.getRow(rowNum).height = 22;
            rowNum++;

            // ═══════════════════════════════════════════════════════════
            // 청구내역 세로 병합 시작점 기록
            // A열: 데이터 첫행 ~ 합계행까지 병합
            // ═══════════════════════════════════════════════════════════
            const cheonguStartRow = rowNum;

            // ═══════════════════════════════════════════════════════════
            // 데이터 행 (rowsWithSubtotal)
            // A: (청구내역 병합)
            // B: ISSUED_DATE
            // C: OUT_PROVIDE_AMOUNT
            // D: OUT_VAT
            // E: OUT_TOTAL_AMOUNT
            // F: PAID_DATE
            // G: PAID_AMOUNT
            // ═══════════════════════════════════════════════════════════
            const MIN_DATA_ROWS = 20;
            const totalDataRows = Math.max(rowsWithSubtotal.length, MIN_DATA_ROWS);

            for (let i = 0; i < totalDataRows; i++) {
                const row = rowsWithSubtotal[i]; // 데이터 없으면 undefined → 빈 행
                const dataRow = worksheet.getRow(rowNum);

                dataRow.getCell(2).value = row?.ISSUED_DATE        || '';
                dataRow.getCell(3).value = row ? parseNumberForExcel(row.OUT_PROVIDE_AMOUNT) : '';
                dataRow.getCell(4).value = row ? parseNumberForExcel(row.OUT_VAT)            : '';
                dataRow.getCell(5).value = row ? parseNumberForExcel(row.OUT_TOTAL_AMOUNT)   : '';
                dataRow.getCell(6).value = row?.PAID_DATE          || '';
                dataRow.getCell(7).value = row ? parseNumberForExcel(row.PAID_AMOUNT)        : '';

                for (let col = 1; col <= 7; col++) {
                    const cell = dataRow.getCell(col);
                    cell.font = { name: '굴림체', size: 10 };
                    cell.alignment = {
                        horizontal: (col >= 3 && col <= 5) || col === 7 ? 'right' : 'center',
                        vertical: 'middle'
                    };
                    if ((col >= 3 && col <= 5) || col === 7) cell.numFmt = '#,##0';
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };
                }
                dataRow.height = 20;
                rowNum++;
            }

            // ═══════════════════════════════════════════════════════════
            // 합계 행 (green)
            // B: "합 계"
            // C: taxInvoiceRow.OUT_PROVIDE_AMOUNT
            // D: taxInvoiceRow.OUT_VAT
            // E: taxInvoiceRow.OUT_TOTAL_AMOUNT
            // F: (연한 주황 빈칸)
            // G: taxInvoiceRow.PAID_AMOUNT (연한 주황)
            // ═══════════════════════════════════════════════════════════
            const sumRowNum = rowNum;
            const sumRow = worksheet.getRow(rowNum);

            sumRow.getCell(2).value = '합 계';
            sumRow.getCell(3).value = parseNumberForExcel(taxInvoiceRow.OUT_PROVIDE_AMOUNT);
            sumRow.getCell(4).value = parseNumberForExcel(taxInvoiceRow.OUT_VAT);
            sumRow.getCell(5).value = parseNumberForExcel(taxInvoiceRow.OUT_TOTAL_AMOUNT);
            sumRow.getCell(6).value = '';
            sumRow.getCell(7).value = parseNumberForExcel(taxInvoiceRow.PAID_AMOUNT);

            for (let col = 1; col <= 7; col++) {
                const cell = sumRow.getCell(col);
                cell.font = { bold: true, name: '굴림체',size: 10 };

                // F, G는 연한 주황 / 나머지는 초록
                const isOrange = col === 6 || col === 7;
                cell.fill = {
                    type: 'pattern', pattern: 'solid',
                    fgColor: { argb: isOrange ? 'FFFFD8C4' : 'FFA8E6A8' }
                };
                cell.alignment = {
                    horizontal: (col >= 3 && col <= 5) || col === 7 ? 'right' : 'center',
                    vertical: 'middle'
                };
                if ((col >= 3 && col <= 5) || col === 7) cell.numFmt = '#,##0';
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            }
            sumRow.height = 22;
            rowNum++;

            // ═══════════════════════════════════════════════════════════
            // A열 세로 병합: 청구내역 (cheonguStartRow ~ sumRowNum)
            // ═══════════════════════════════════════════════════════════
            worksheet.mergeCells(`A${cheonguStartRow}:A${sumRowNum}`);
            const cheonguCell = worksheet.getCell(`A${cheonguStartRow}`);
            cheonguCell.value = '청구내역';
            cheonguCell.font = { size: 10, name: '굴림체' };
            cheonguCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cheonguCell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };

            // ═══════════════════════════════════════════════════════════
            // 미청구액 행 (파란 텍스트)
            // A:B 병합 → "미청구액"
            // C: PROVIDE_AMOUNT - OUT_PROVIDE_AMOUNT
            // D: VAT - OUT_VAT
            // E: TOTAL_AMOUNT - OUT_TOTAL_AMOUNT
            // F: "미수금"
            // G: taxInvoiceRow.UNPAID_AMOUNT_OUT
            // ═══════════════════════════════════════════════════════════
            worksheet.mergeCells(`A${rowNum}:B${rowNum}`);
            const unpaidRow = worksheet.getRow(rowNum);

            unpaidRow.getCell(1).value = '미청구액';
            unpaidRow.getCell(3).value = contractProvide - parseNumberForExcel(taxInvoiceRow.OUT_PROVIDE_AMOUNT);
            unpaidRow.getCell(4).value = contractVat     - parseNumberForExcel(taxInvoiceRow.OUT_VAT);
            unpaidRow.getCell(5).value = contractTotal   - parseNumberForExcel(taxInvoiceRow.OUT_TOTAL_AMOUNT);
            unpaidRow.getCell(6).value = '미수금';
            unpaidRow.getCell(7).value = parseNumberForExcel(taxInvoiceRow.UNPAID_AMOUNT_OUT);

            for (let col = 1; col <= 7; col++) {
                const cell = unpaidRow.getCell(col);
                cell.font = { color: { argb: 'FF0048FF' }, size: 10, name: '굴림체' };
                cell.alignment = {
                    horizontal: (col >= 3 && col <= 5) || col === 7 ? 'right' : 'center',
                    vertical: 'middle'
                };
                if ((col >= 3 && col <= 5) || col === 7) cell.numFmt = '#,##0';
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            }
            worksheet.getRow(rowNum).height = 22;

            // ═══════════════════════════════════════════════════════════
            // 표 전체 바깥 테두리 두껍게
            // ═══════════════════════════════════════════════════════════
            const tableStartRow = headerRow1;
            const tableEndRow   = rowNum;

            for (let r = tableStartRow; r <= tableEndRow; r++) {
                for (let c = 1; c <= 7; c++) {
                    const cell = worksheet.getCell(r, c);
                    const border = { ...cell.border };
                    if (r === tableStartRow) border.top    = { style: 'thin', color: { argb: 'FF000000' } };
                    if (r === tableEndRow)   border.bottom = { style: 'thin', color: { argb: 'FF000000' } };
                    if (c === 1)             border.left   = { style: 'thin', color: { argb: 'FF000000' } };
                    if (c === 7)             border.right  = { style: 'thin', color: { argb: 'FF000000' } };
                    cell.border = border;
                }
            }

            // ═══════════════════════════════════════════════════════════
            // 파일 저장
            // ═══════════════════════════════════════════════════════════
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, `매출정산서_${fieldName}_${format(new Date(), 'yyyyMMdd')}.xlsx`);

        } catch (error) {
            console.error('엑셀 내보내기 오류:', error);
            alert('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };
    // 인쇄
    const handlePrint = () => {
        const printContents = document.getElementById('print-area').innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload();
    };

    // =========================
    // 렌더링
    // =========================
    return (
        <div className="grid-container">
            <h2>매출정산서</h2>

            <div className="gh-band">
                <div className="gh-band__row">
                    <div className="gh-band__title">
                        <span className="gh-octo" aria-hidden>▶</span>
                        <span>조회조건</span>
                    </div>
                </div>
                <div className="gh-band__filters">

                    <div className="filter-item gh-field">
                        <label>현장</label>
                        <Autocomplete
                            options={fieldOptions}
                            value={fieldOptions.find((option) => option.FIELD_ID === fieldId) || null}
                            onChange={(event, newValue) => {
                                setFieldId(newValue ? newValue.FIELD_ID : '');
                            }}
                            getOptionLabel={(option) => option?.FIELD_NAME || ''}
                            isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="현장 선택"
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    sx={{ px: 1, minWidth: 200 }}
                                />
                            )}
                        />
                    </div>
                    <div className="filter-actions gh-actions">
                        <button className="btn" onClick={searchList}>조회</button>
                    </div>
                </div>
            </div>

            <div className="button-group-right">
                <button onClick={exportToExcel} className="btn btn-primary">엑셀 내려받기</button>
                <button onClick={handlePrint} className="btn btn-ghost">출력하기</button>
            </div>

            <div id="print-area">
                <div style={{ border: '1px solid black', textAlign: 'center' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold'}}>매 출 정 산 서</span>
                </div>
                <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                    {rows.length > 0
                        ? `현장명: ${fieldOptions.find(f => f.FIELD_ID === fieldId)?.FIELD_NAME || ''}`
                        : ''
                    }

                </div>

                <div className="grid-table-wrapper">
                    <table className="grid-table">
                        <colgroup>
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '15%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th rowSpan="2" colSpan="2"><strong>계약금액</strong></th>
                                <th><strong>공 급 가 액</strong></th>
                                <th><strong>부 가 세</strong></th>
                                <th><strong>합 계</strong></th>
                                <th style={{ background: '#a8e6a8' }} rowSpan="2">입금일자</th>
                                <th style={{ background: '#a8e6a8' }} rowSpan="2">입금액</th>
                            </tr>
                            <tr>
                                <th style={{ background: '#a8e6a8' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].PROVIDE_AMOUNT) : ''}</span></th>
                                <th style={{ background: '#a8e6a8' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].VAT) : ''}</span></th>
                                <th style={{ background: '#a8e6a8' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].TOTAL_AMOUNT) : ''}</span></th>
                            </tr>
                        </thead>
                        <tbody>

                                <tr >
                                    <td rowSpan={Math.max(rowsWithSubtotal.length, 20) + 2}>
                                        <strong>청구내역</strong>
                                    </td>
                                </tr>

                            {(() => {
                                const result = [];
                                let i = 0;
                                let dataRowCount = 0; // 실제 렌더된 행 수 카운트

                                while (i < rowsWithSubtotal.length) {
                                    const currentRow = rowsWithSubtotal[i];
                                    const currentMonth = currentRow.ISSUED_MONTH;

                                    let spanCount = 0;
                                    for (let j = i; j < rowsWithSubtotal.length; j++) {
                                        if (rowsWithSubtotal[j].ISSUED_MONTH === currentMonth) spanCount++;
                                        else break;
                                    }

                                    for (let k = 0; k < spanCount; k++) {
                                        const row = rowsWithSubtotal[i + k];
                                        const isSubtotal = row.isSubtotal;

                                        dataRowCount++; // 카운트 증가

                                        result.push(
                                            <tr key={i + k} className={row.isEtctotal ? 'etctotal-row' : (isSubtotal ? 'subtotal-row' : '')}>
                                                <td>{row.ISSUED_DATE}</td>
                                                <td><span className="right-align">{formatNumber(row.OUT_PROVIDE_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.OUT_VAT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.OUT_TOTAL_AMOUNT)}</span></td>
                                                <td>{row.PAID_DATE}</td>
                                                <td><span className="right-align">{formatNumber(row.PAID_AMOUNT)}</span></td>
                                            </tr>
                                        );
                                    }
                                    i += spanCount;
                                }

                                // 최소 15줄 보장 - 빈 행 채우기
                                const MIN_ROWS = 20;
                                const emptyRowCount = Math.max(0, MIN_ROWS - dataRowCount);
                                for (let e = 0; e < emptyRowCount; e++) {
                                    result.push(
                                        <tr key={`empty-${e}`}>
                                            <td>&nbsp;</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    );
                                }

                                return result;
                            })()}

                            {/* 3. 전체 합계 */}
                                <tr style={{marginTop: '50px'}}>
                                    <td style={{ background: '#a8e6a8' }}><strong>합 계</strong></td>
                                    <td style={{ background: '#a8e6a8' }}><strong><span className="right-align">{formatNumber(taxInvoiceRow.OUT_PROVIDE_AMOUNT)}</span></strong></td>
                                    <td style={{ background: '#a8e6a8' }}><strong><span className="right-align">{formatNumber(taxInvoiceRow.OUT_VAT)}</span></strong></td>
                                    <td style={{ background: '#a8e6a8' }}><strong><span className="right-align">{formatNumber(taxInvoiceRow.OUT_TOTAL_AMOUNT)}</span></strong></td>
                                    <td style={{ background: '#ffd8c4' }}><span></span></td>
                                    <td style={{ background: '#ffd8c4' }}><span className="right-align">{formatNumber(taxInvoiceRow.PAID_AMOUNT)}</span></td>
                                </tr>

                                <tr>
                                    <td style={{ color: '#0048ff' }} colSpan="2">미청구액</td>
                                    <td style={{ color: '#0048ff' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].PROVIDE_AMOUNT-taxInvoiceRow.OUT_PROVIDE_AMOUNT) : ''} </span></td>
                                    <td style={{ color: '#0048ff' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].VAT-taxInvoiceRow.OUT_VAT) : ''} </span></td>
                                    <td style={{ color: '#0048ff' }}><span className="right-align">{rows?.length > 0 ? formatNumber(rows[0].TOTAL_AMOUNT-taxInvoiceRow.OUT_TOTAL_AMOUNT) : ''} </span></td>
                                    <td style={{ color: '#0048ff' }}>미수금</td>
                                    <td><span className="right-align">{rows?.length > 0 ? formatNumber(taxInvoiceRow.UNPAID_AMOUNT_OUT) : ''} </span></td>
                                </tr>

                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    );
};

export default Total1;